import { useContainerWidth } from "./flowsChartShared";
import type { EquityCurvePoint } from "../api";

/**
 * Forward-equity-record (2026-10-27): the book's own mark-to-market
 * series vs. NIFTY and the equal-weight comparator. Hand-rolled SVG, same
 * justification as ValuationBandChart.tsx's own deviation from react-
 * financial-charts -- three simple polylines + one legend, no crosshair/
 * zoom/pan, nothing RFC's OHLC-oriented API is built for. Colours reused
 * from the project's existing chart palette (flowsChartShared's
 * FII_COLOR/NET_COLOR), not new ones, for visual consistency across pages.
 */

const HEIGHT = 220;
const MARGIN = { top: 10, right: 12, bottom: 22, left: 44 };

const BOOK_COLOR = "#1a1d24";
const BENCHMARK_COLOR = "#3467c9"; // reused from FlowsChart's FII_COLOR
const EQUAL_WEIGHT_COLOR = "#6a3fa0"; // reused from FlowsChart's NET_COLOR

function formatTick(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function pctFmt(v: number): string {
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`;
}

export function EquityCurveChart({ series }: { series: EquityCurvePoint[] }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  if (series.length < 2) {
    return (
      <div ref={ref} className="empty-state" style={{ minHeight: 80 }}>
        Not enough history yet for a curve -- only {series.length} day(s) tracked.
      </div>
    );
  }
  if (width === 0) {
    return <div ref={ref} style={{ minHeight: HEIGHT }} />;
  }

  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;

  const lines: { key: "book_equity" | "benchmark_equity" | "equal_weight_equity"; color: string; label: string }[] = [
    { key: "book_equity", color: BOOK_COLOR, label: "Book" },
    { key: "benchmark_equity", color: BENCHMARK_COLOR, label: "NIFTY" },
    { key: "equal_weight_equity", color: EQUAL_WEIGHT_COLOR, label: "Equal-weight" },
  ];

  const allValues: number[] = [];
  for (const l of lines) {
    for (const p of series) {
      const v = p[l.key];
      if (v !== null && v !== undefined) allValues.push(v);
    }
  }
  const yMin = Math.min(1.0, ...allValues);
  const yMax = Math.max(1.0, ...allValues);
  const yPad = (yMax - yMin) * 0.08 || 0.02;
  const domainMin = yMin - yPad;
  const domainMax = yMax + yPad;

  const xAt = (i: number) => MARGIN.left + (i / (series.length - 1)) * plotW;
  const yAt = (v: number) => MARGIN.top + (1 - (v - domainMin) / (domainMax - domainMin)) * plotH;

  const pathFor = (key: "book_equity" | "benchmark_equity" | "equal_weight_equity") => {
    let d = "";
    let started = false;
    series.forEach((p, i) => {
      const v = p[key];
      if (v === null || v === undefined) return;
      d += `${started ? "L" : "M"}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)} `;
      started = true;
    });
    return d;
  };

  const tickCount = Math.min(6, series.length);
  const tickIndices = Array.from({ length: tickCount }, (_, i) =>
    Math.round((i / Math.max(tickCount - 1, 1)) * (series.length - 1)));

  const baselineY = yAt(1.0);

  return (
    <div ref={ref}>
      <svg width={width} height={HEIGHT} role="img" aria-label="Book equity vs. benchmark and equal-weight comparator">
        {/* Baseline at 1.0 (go-live, 0% cumulative return). */}
        <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={baselineY} y2={baselineY}
              stroke="#c7cbd4" strokeWidth={1} strokeDasharray="3 3" />
        <text x={MARGIN.left - 4} y={baselineY + 3} fontSize={9} fill="#8a91a0" textAnchor="end">1.00</text>

        {lines.map((l) => (
          <path key={l.key} d={pathFor(l.key)} fill="none" stroke={l.color} strokeWidth={1.6} />
        ))}

        {tickIndices.map((i) => (
          <text key={i} x={xAt(i)} y={HEIGHT - 6} fontSize={9} fill="#8a91a0" textAnchor="middle">
            {formatTick(series[i].as_of_date)}
          </text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: 16, fontSize: "0.72rem", marginTop: 4 }}>
        {lines.map((l) => {
          const last = [...series].reverse().find((p) => p[l.key] !== null && p[l.key] !== undefined);
          const v = last ? (last[l.key] as number) : null;
          return (
            <span key={l.key} style={{ color: l.color, fontWeight: 600 }}>
              &#9632; {l.label}{v !== null ? ` ${pctFmt(v - 1.0)}` : ""}
            </span>
          );
        })}
      </div>
    </div>
  );
}
